import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  Slide,
  styled,
  Typography,
} from '@mui/material';
import React, {
  forwardRef, useEffect, useReducer, useState,
} from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { modelMap } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { AsyncMultipleSelect } from 'shared/components/select/SearchableSelects';
import { searchBuildings } from 'shared/api/buildings';
import { searchComplexes } from 'shared/api/complexes';
import { searchManagers } from 'shared/api/managers';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'property':
      return {
        ...state,
        property: payload,
      };

    case 'complex':
      return {
        ...state,
        complex: payload,
      };

    case 'manager':
      return {
        ...state,
        manager: payload,
      };

    case 'reset':
      return {
        complex: [],
        manager: [],
        property: [],
      };

    default:
      return state;
  }
}

const ValidFor = styled(Box)(({ theme }) => ({
  backgroundColor: (theme.vars || theme).palette.primary.pale,
  color: (theme.vars || theme).palette.text.secondary,
  borderRadius: '.25rem !important',
  marginTop: '5px',
  marginBottom: '2px',
  paddingTop: '10px',
  paddingBottom: '10px',
}));

/**
 * @param {Object} props
 * @param {Function} props.setRefiner
 * @param {Object} props.results
 * @param {string} props.modelKey
 */
export default function Refiner(props) {
  const [show, setShow] = useState(false);
  const [conditionalSearch, setConditionalSearch] = useState(false);
  const isMobile = useMediaQuery('(max-width:420px)');

  const [state, dispatch] = useReducer(reducer, {
    complex: [],
    manager: [],
    property: [],
  });

  const {
    setRefiner,
    results,
    modelKey,
  } = props;

  useEffect(() => {
    setRefiner(state.property, 'property');
    setRefiner(state.complex, 'complex');
    setRefiner(state.manager, 'manager');
    // setRefiner is recreated by the parent each render; depending on it would
    // re-run (and re-dispatch) every render. This effect only needs to sync on state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const complexBaseSearch = `${state.manager.map((manager) => `manager_id=${manager.id}`).join('&')}`;

  const buildingBaseSearch = state.complex?.length > 0
    ? `${state.complex.map((complex) => `complex_id=${complex.id}`).join('&')}`
    : `${state.manager.map((manager) => `manager_id=${manager.id}`).join('&')}`;

  const validForLinks = () => {
    const rules = results[modelKey];
    if (rules.length > 0) {
      return (
        <>
          <Typography variant="body2" align="center" component="div">
            This coupon will be valid for:
          </Typography>
          <Typography variant="body3" align="center" component="div">
            {rules.map((item, index) => (
              <Link target="_blank" key={item.id} to={modelMap[modelKey].getView(item.id)}>{ (index < rules.length - 1) ? `${item.name}, ` : item.name}</Link>))}
          </Typography>
        </>
      );
    }
    return null;
  };

  return (
    <>
      <Button
        variant={VARIANTS.outlined}
        onClick={() => setShow(true)}
      >
        Target Customers
      </Button>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        fullWidth
        fullScreen={isMobile}
        slots={{
          transition: isMobile ? Transition : Fade,
        }}
      >
        <DialogTitle>
          Select Target Customers
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={(
              <Checkbox
                checked={conditionalSearch}
                onChange={() => setConditionalSearch(!conditionalSearch)}
              />
            )}
            label="Filter Dependent Selections"
          />
          <AsyncMultipleSelect
            label={modelMap.manager.title}
            searchRequest={searchManagers}
            onChange={(e) => dispatch({ action: 'manager', payload: e })}
            selectedOptions={state.manager}
            placeholder={modelMap.manager.placeholder}
          />
          <AsyncMultipleSelect
            label={modelMap.complex.title}
            searchRequest={searchComplexes}
            onChange={(e) => dispatch({ action: 'complex', payload: e })}
            selectedOptions={state.complex}
            baseSearch={conditionalSearch ? complexBaseSearch : ''}
            placeholder={modelMap.complex.placeholder}
          />
          <AsyncMultipleSelect
            label={modelMap.property.title}
            searchRequest={searchBuildings}
            onChange={(e) => dispatch({ action: 'property', payload: e })}
            selectedOptions={state.property}
            baseSearch={conditionalSearch ? buildingBaseSearch : ''}
            placeholder={modelMap.property.placeholder}
          />
          <ValidFor>
            {validForLinks()}
            <Typography variant="body2" align="center" component="div">
              Coupons are applied to the most specific selections.
            </Typography>
          </ValidFor>
        </DialogContent>
        <DialogActions>
          <Button
            color={BUTTON_COLORS.primary}
            variant={VARIANTS.outlined}
            disabled={results[modelKey].length === 0}
            onClick={() => dispatch({ action: 'reset' })}
          >
            Reset
          </Button>
          <Button
            color={BUTTON_COLORS.primary}
            variant={VARIANTS.contained}
            onClick={() => setShow(false)}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
